import { useCallback, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase-config.js";
import { useDarkMode } from "./useDarkMode";

export const LIGHT_BACKGROUNDS = Object.freeze([
  { file: "/backgrounds/1.jpg", label: "Uni", streakRequired: 0 },
  { file: "/backgrounds/2.jpg", label: "Ciel lumineux", streakRequired: 0 },
  { file: "/backgrounds/6.jpg", label: "Horizon marin", streakRequired: 0 },
  { file: "/backgrounds/7.jpg", label: "Pétales", streakRequired: 0 },
  { file: "/backgrounds/15.jpg", label: "Neige", streakRequired: 0 },
  { file: "/backgrounds/3.jpg", label: "Forêt brumeuse", streakRequired: 7 },
  { file: "/backgrounds/4.jpg", label: "Dunes claires", streakRequired: 21 },
  { file: "/backgrounds/5.jpg", label: "Eau claire", streakRequired: 60 },
]);

export const DARK_BACKGROUNDS = Object.freeze([
  { file: "/backgrounds/8.jpg", label: "Uni", streakRequired: 0 },
  { file: "/backgrounds/9.jpg", label: "Ciel étoilé", streakRequired: 0 },
  { file: "/backgrounds/13.jpg", label: "Lune", streakRequired: 0 },
  { file: "/backgrounds/14.jpg", label: "Sommets de nuit", streakRequired: 0 },
  { file: "/backgrounds/16.jpg", label: "Sable noir", streakRequired: 0 },
  { file: "/backgrounds/10.jpg", label: "Aurore boréale", streakRequired: 7 },
  { file: "/backgrounds/11.jpg", label: "Océan nocturne", streakRequired: 21 },
  { file: "/backgrounds/12.jpg", label: "Forêt sombre", streakRequired: 60 },
]);

export const LIGHT_BACKGROUNDS_DESKTOP = Object.freeze([
  { file: "/backgrounds/1.jpg", label: "Uni", streakRequired: 0 },
  { file: "/backgrounds/2b.jpg", label: "Ciel lumineux", streakRequired: 0 },
  { file: "/backgrounds/6b.jpg", label: "Horizon marin", streakRequired: 0 },
  { file: "/backgrounds/7b.jpg", label: "Pétales", streakRequired: 0 },
  { file: "/backgrounds/15b.jpg", label: "Neige", streakRequired: 0 },
  { file: "/backgrounds/3b.jpg", label: "Forêt brumeuse", streakRequired: 7 },
  { file: "/backgrounds/4b.jpg", label: "Dunes claires", streakRequired: 21 },
  { file: "/backgrounds/5b.jpg", label: "Eau claire", streakRequired: 60 },
]);

export const DARK_BACKGROUNDS_DESKTOP = Object.freeze([
  { file: "/backgrounds/8b.jpg", label: "Uni", streakRequired: 0 },
  { file: "/backgrounds/9b.jpg", label: "Ciel étoilé", streakRequired: 0 },
  { file: "/backgrounds/13b.jpg", label: "Lune", streakRequired: 0 },
  { file: "/backgrounds/14b.jpg", label: "Sommets de nuit", streakRequired: 0 },
  { file: "/backgrounds/16b.jpg", label: "Sable noir", streakRequired: 0 },
  { file: "/backgrounds/10b.jpg", label: "Aurore boréale", streakRequired: 7 },
  { file: "/backgrounds/11b.jpg", label: "Océan nocturne", streakRequired: 21 },
  { file: "/backgrounds/12b.jpg", label: "Forêt sombre", streakRequired: 60 },
]);

const BACKGROUND_CHANGE_EVENT = "harmonauria:background-change";

function normalizeBackgroundIndex(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export function useBackground(isPremium = false) {
  const { isDarkMode } = useDarkMode();
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const [backgroundPreferences, setBackgroundPreferences] = useState({ uid: null, lightIndex: 0, darkIndex: 0 });

  useEffect(() => {
    return onAuthStateChanged(auth, (currentUser) => {
      setUid(currentUser?.uid || null);
    });
  }, []);

  useEffect(() => {
    if (!uid) {
      setBackgroundPreferences({ uid: null, lightIndex: 0, darkIndex: 0 });
      return undefined;
    }

    setBackgroundPreferences({ uid: null, lightIndex: 0, darkIndex: 0 });

    const userRef = doc(db, "users", uid);

    return onSnapshot(userRef, (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      const saved = data?.uiPreferences?.backgrounds || {};

      setBackgroundPreferences({
        uid,
        lightIndex: normalizeBackgroundIndex(saved.lightIndex),
        darkIndex: normalizeBackgroundIndex(saved.darkIndex),
      });
    }, (error) => {
      console.warn("Impossible de charger les fonds du compte.", error);
      setBackgroundPreferences({ uid, lightIndex: 0, darkIndex: 0 });
    });
  }, [uid]);

  useEffect(() => {
    try {
      localStorage.removeItem("harmonauria_premium_bg_access");
      localStorage.removeItem("harmonauria_bg_index");
      localStorage.removeItem("harmonauria_bg_index_light");
      localStorage.removeItem("harmonauria_bg_index_dark");
    } catch {
      /* noop */
    }
  }, []);

  const baseBackgrounds = isDarkMode ? DARK_BACKGROUNDS : LIGHT_BACKGROUNDS;
  const baseDesktopBackgrounds = isDarkMode ? DARK_BACKGROUNDS_DESKTOP : LIGHT_BACKGROUNDS_DESKTOP;

  const backgrounds = useMemo(() => {
    return isPremium ? baseBackgrounds.map((background) => ({ ...background, streakRequired: 0 })) : baseBackgrounds;
  }, [baseBackgrounds, isPremium]);

  const desktopBackgrounds = useMemo(() => {
    return isPremium ? baseDesktopBackgrounds.map((background) => ({ ...background, streakRequired: 0 })) : baseDesktopBackgrounds;
  }, [baseDesktopBackgrounds, isPremium]);

  const preferenceKey = isDarkMode ? "darkIndex" : "lightIndex";
  const storedIndex = backgroundPreferences.uid === uid ? backgroundPreferences[preferenceKey] : 0;
  const safeIndex = Math.max(0, Math.min(storedIndex, backgrounds.length - 1));

  useEffect(() => {
    const handleBackgroundChange = (event) => {
      const detail = event.detail;
      if (!detail || detail.uid !== uid || detail.isDarkMode !== isDarkMode) return;
      if (!Number.isFinite(detail.index)) return;

      setBackgroundPreferences((current) => {
        if (current.uid !== uid) return current;
        return { ...current, [preferenceKey]: Math.max(0, detail.index) };
      });
    };

    window.addEventListener(BACKGROUND_CHANGE_EVENT, handleBackgroundChange);
    return () => window.removeEventListener(BACKGROUND_CHANGE_EVENT, handleBackgroundChange);
  }, [isDarkMode, preferenceKey, uid]);

  const selectBg = useCallback((index) => {
    if (!uid) return;

    const nextIndex = Math.max(0, Math.min(Number(index) || 0, backgrounds.length - 1));
    const field = isDarkMode ? "darkIndex" : "lightIndex";

    setBackgroundPreferences((current) => ({
      uid,
      lightIndex: current.uid === uid ? current.lightIndex : 0,
      darkIndex: current.uid === uid ? current.darkIndex : 0,
      [field]: nextIndex,
    }));

    window.dispatchEvent(new CustomEvent(BACKGROUND_CHANGE_EVENT, {
      detail: { uid, index: nextIndex, isDarkMode },
    }));

    void updateDoc(doc(db, "users", uid), {
      [`uiPreferences.backgrounds.${field}`]: nextIndex,
    }).catch((error) => {
      console.warn("Impossible d’enregistrer le fond du compte.", error);
    });
  }, [backgrounds.length, isDarkMode, uid]);

  return {
    bgIndex: safeIndex,
    selectBg,
    backgrounds,
    desktopBackgrounds,
    imageFiles: backgrounds.map((background) => background.file),
    desktopImageFiles: desktopBackgrounds.map((background) => background.file),
  };
}